/* eslint-disable react-hooks/exhaustive-deps */
/* ============================================================
   หน้าข้อมูลหลักแบบ "รายการ + ฟอร์มใน Modal" ใช้ซ้ำได้ทุก master
   (ลูกค้า / แบบบ้าน / หมวดวัสดุ / วัสดุ)

   <CrudPage
      title="ข้อมูลลูกค้า"  menu="support_customer"  rowKey="cus_code"
      service={CustomerService}
      columns={(actions) => [...]}       // actions = { edit(record) }
      renderForm={(form, editing) => <>...Form.Item...</>}
      filters={<Select .../>}             // ช่องกรองเพิ่มเติม (ค่าเก็บใน extraParams)
   />
============================================================ */
import React, { useEffect, useMemo, useState } from "react";
import {
  Button,
  Card,
  Flex,
  Form,
  Input,
  Modal,
  Popconfirm,
  Select,
  Space,
  Table,
  Tooltip,
  Typography,
  message,
} from "antd";
import { EditOutlined, PlusOutlined, SearchOutlined, StopOutlined } from "@ant-design/icons";
import { usePermission } from "../../store/context/permission-context";
import { apiError } from "../../service/Ak.service";

export default function CrudPage({
  title,
  subtitle,
  menu,
  rowKey,
  service,
  columns,
  renderForm,
  toPayload = (v) => v,
  toForm = (r) => r,
  initialValues = {},
  modalWidth = 720,
  extraFilters = null,
  extraParams = {},
  activeFilter = true,
  addText = "เพิ่ม",
  deleteText = "ปิดใช้งาน",
  deleteConfirm = "ปิดการใช้งานรายการนี้?",
  canDeleteRecord = (r) => r?.active_status !== "N",
  reloadKey = 0,
}) {
  const { can } = usePermission();
  const [form] = Form.useForm();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [q, setQ] = useState("");
  const [active, setActive] = useState(activeFilter ? "Y" : undefined);
  const [editing, setEditing] = useState(null); // null = ปิด , {} = เพิ่ม , record = แก้ไข
  const [saving, setSaving] = useState(false);
  const [formInit, setFormInit] = useState({});
  const [formKey, setFormKey] = useState(0);

  const load = () => {
    setLoading(true);
    service
      .list({ q: q || undefined, active, ...extraParams }, { ignoreLoading: true })
      .then((res) => setRows(res.data?.data || []))
      .catch((e) => message.error(apiError(e, "โหลดข้อมูลไม่สำเร็จ")))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
  }, [q, active, JSON.stringify(extraParams), reloadKey]);

  const openForm = (values, record) => {
    setFormInit(values);
    setFormKey((k) => k + 1);
    setEditing(record);
  };

  const openAdd = () => openForm(initialValues, {});

  /* ฟอร์มใช้ instance เดิมซ้ำ — ล้างค่าเก่าให้เหลือค่าตั้งต้นของรายการที่เพิ่งเปิด */
  useEffect(() => {
    if (formKey) form.resetFields();
  }, [formKey]);

  const openEdit = (record) => {
    /* ดึงรายละเอียดล่าสุดก่อนแก้ */
    service
      .get(record[rowKey])
      .then((res) => {
        const r = res.data?.data || record;
        openForm(toForm(r), r);
      })
      .catch((e) => message.error(apiError(e)));
  };

  const isEdit = editing && editing[rowKey];

  const save = async () => {
    const values = await form.validateFields();
    setSaving(true);
    const body = toPayload({ ...values, ...(isEdit ? { [rowKey]: editing[rowKey] } : {}) }, editing);
    try {
      await (isEdit ? service.update(body) : service.create(body));
      message.success("บันทึกเรียบร้อย");
      setEditing(null);
      load();
    } catch (e) {
      message.error(apiError(e, "บันทึกไม่สำเร็จ"));
    } finally {
      setSaving(false);
    }
  };

  const deactivate = (record) =>
    service
      .remove(record[rowKey])
      .then(() => {
        message.success("เรียบร้อย");
        load();
      })
      .catch((e) => message.error(apiError(e)));

  const allColumns = useMemo(() => {
    const base = columns({ edit: openEdit });
    const canEdit = can(menu, "edit");
    const canDel = can(menu, "delete");
    if (!canEdit && !canDel) return base;
    return [
      ...base,
      {
        title: "",
        key: "operation",
        width: 96,
        fixed: "right",
        render: (_, r) => (
          <Space size={4}>
            {canEdit && (
              <Tooltip title="แก้ไข">
                <Button size="small" icon={<EditOutlined />} onClick={() => openEdit(r)} />
              </Tooltip>
            )}
            {canDel && canDeleteRecord(r) && (
              <Popconfirm title={deleteConfirm} onConfirm={() => deactivate(r)} okText="ยืนยัน" cancelText="ไม่">
                <Tooltip title={deleteText}>
                  <Button size="small" danger icon={<StopOutlined />} />
                </Tooltip>
              </Popconfirm>
            )}
          </Space>
        ),
      },
    ];
  }, [columns, menu, can]);

  return (
    <div className="layout-content px-3 sm:px-5 md:px-5">
      <Card className="ak-card">
        <Flex justify="space-between" align="center" wrap="wrap" gap={8} className="mb-3">
          <div>
            <Typography.Title level={3} className="m-0 !text-zinc-800">
              {title}
            </Typography.Title>
            {subtitle && <Typography.Text type="secondary">{subtitle}</Typography.Text>}
          </div>
          {can(menu, "add") && (
            <Button type="primary" icon={<PlusOutlined />} onClick={openAdd}>
              {addText}
            </Button>
          )}
        </Flex>

        <Flex gap={8} wrap="wrap" className="mb-3">
          <Input
            allowClear
            prefix={<SearchOutlined />}
            placeholder="ค้นหา"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            style={{ width: 260 }}
          />
          {extraFilters}
          {activeFilter && (
            <Select
              value={active}
              onChange={setActive}
              allowClear
              placeholder="สถานะทั้งหมด"
              style={{ width: 160 }}
              options={[
                { value: "Y", label: "ใช้งานอยู่" },
                { value: "N", label: "ปิดใช้งาน" },
              ]}
            />
          )}
        </Flex>

        <Table
          rowKey={rowKey}
          size="small"
          loading={loading}
          columns={allColumns}
          dataSource={rows}
          scroll={{ x: "max-content" }}
          pagination={{ pageSize: 25, showSizeChanger: true, showTotal: (t) => `ทั้งหมด ${t} รายการ` }}
        />
      </Card>

      <Modal
        open={editing !== null}
        title={isEdit ? `แก้ไข${title.replace(/^ข้อมูล/, "")}` : `${addText}`}
        onCancel={() => setEditing(null)}
        onOk={save}
        okText="บันทึก"
        cancelText="ยกเลิก"
        confirmLoading={saving}
        width={modalWidth}
        maskClosable={false}
        forceRender
      >
        <Form form={form} key={formKey} layout="vertical" autoComplete="off" initialValues={formInit}>
          {editing !== null && renderForm(form, isEdit ? editing : null)}
        </Form>
      </Modal>
    </div>
  );
}
